import { catchError, mergeMap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';
import { EntityBaseState } from '../base/entity.base.state';

export const STAIN_ENTITY_TOKEN = new StateToken<StainEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.STAIN_ENTITY);

export type StainEntityStateModel = FlatModels.EntityStatesModels.EntityStateModel<FlatModels.StainEntityModels.Stain>;

@State<StainEntityStateModel>({
  name: STAIN_ENTITY_TOKEN,
  defaults: {
    entities: {},
  }
})
@Injectable()
export class StainEntityState extends EntityBaseState<FlatModels.StainEntityModels.Stain, StainEntityStateModel>{
  constructor(protected store: Store,
              protected apiControlService: ApiControlService) {
    super();
  }

  //<editor-fold desc="Selectors">
	
  @Selector([StainEntityState])
  static override typedEntities$(state: StainEntityStateModel): { [id: string]: FlatModels.StainEntityModels.Stain } {
    return EntityBaseState.typedEntities$(state);
  }
	
  @Selector([StainEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.StainEntityModels.Stain }): FlatModels.StainEntityModels.Stain[] {
    return EntityBaseState.entities$(typedEntities);
  }
	
  @Selector([StainEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.StainEntityModels.Stain }) {
    return EntityBaseState.entity$(typedEntities);
  }
	
  @Selector([StainEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.StainEntityModels.Stain }) {
    return EntityBaseState.entitiesByIds$(typedEntities);
  }
  
  @Selector([StainEntityState.entities$])
  static stainArray$(entities: FlatModels.StainEntityModels.Stain[]): {name: string, value: string}[] {
    return entities.reduce((arr, stain) => {
      arr.push({
        value: stain.id,
        name: stain.name
      });
      return arr;
    }, [] as { value: string, name: string }[]);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">
	
  @Action(FlatActions.StainEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<StainEntityStateModel>,
                            action: FlatActions.StainEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.StainEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<StainEntityStateModel>,
                              action: FlatActions.StainEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.StainEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<StainEntityStateModel>,
                               action: FlatActions.StainEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.StainEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<StainEntityStateModel>,
                                 action: FlatActions.StainEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
	
  @Action(FlatActions.StainEntityActions.AddEntity)
  addEntity(_: StateContext<StainEntityStateModel>,
            {entity}: FlatActions.StainEntityActions.AddEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.StainEntityState.addEntity"
      );
    }

    // Add Stain
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.STAIN_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.StainEntityActions.UpdateEntity)
  updateEntity(_: StateContext<StainEntityStateModel>,
               {entity}: FlatActions.StainEntityActions.UpdateEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.StainEntityState.updateEntity"
      );
    }

    // Update Stain
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.STAIN_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  @Action(FlatActions.StainEntityActions.RemoveEntity)
  removeEntity(_: StateContext<StainEntityStateModel>,
               {entity}: FlatActions.StainEntityActions.RemoveEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.StainEntityState.removeEntity"
      );
    }

    // Remove Stain
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.STAIN_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  //</editor-fold>

  //</editor-fold>
}
